import { router } from "expo-router";

import i18n from "@/i18n/i18n";
import { formatDisplayDate, formatIsoDate, toDateOnlyIso } from "@/lib/date";
import { showAlert } from "@/lib/showAlert";
import { TEST_IDS } from "@/testIdDefinition";

type AbsenceReminderPromptInput = {
    childId: number;
    classDate?: string;
    /** First name from the notification payload (`data.child_name`). */
    childName?: string;
};

/**
 * True when `classDate` is the local calendar today (YYYY-MM-DD).
 */
export function isClassDateToday(classDate: string): boolean {
    return toDateOnlyIso(classDate) === formatIsoDate(new Date());
}

/**
 * Resolves the display name used in the prompt (payload or fallback).
 */
function resolveChildName(childName?: string): string {
    return childName?.trim() || i18n.t("absenceReminder.childFallback");
}

/** Builds the alert title from the child name. */
export function buildAbsenceReminderTitle(input: {
    childName?: string;
}): string {
    return i18n.t("absenceReminder.title", {
        childName: resolveChildName(input.childName),
    });
}

/**
 * Builds the absence-reminder dialog body with today vs dated wording.
 */
export function buildAbsenceReminderMessage(input: {
    childName?: string;
    classDate?: string;
}): string {
    const childName = resolveChildName(input.childName);

    if (input.classDate && !isClassDateToday(input.classDate)) {
        return i18n.t("absenceReminder.messageOnDate", {
            childName,
            date: formatDisplayDate(input.classDate),
        });
    }

    return i18n.t("absenceReminder.messageToday", { childName });
}

/**
 * Shows the absence-reminder prompt; primary action opens a prefilled leave request.
 */
export function showAbsenceReminderPrompt(
    input: AbsenceReminderPromptInput,
): void {
    const classDate =
        typeof input.classDate === "string" &&
        /^\d{4}-\d{2}-\d{2}/.test(input.classDate)
            ? toDateOnlyIso(input.classDate)
            : undefined;

    showAlert(
        buildAbsenceReminderTitle({ childName: input.childName }),
        buildAbsenceReminderMessage({
            childName: input.childName,
            classDate,
        }),
        [
            {
                text: i18n.t("absenceReminder.dismiss"),
                variant: "outline",
                testID: TEST_IDS[TEST_IDS.ABSENCE_REMINDER_DISMISS_BUTTON],
            },
            {
                text: i18n.t("absences.newRequest"),
                variant: "default",
                testID: TEST_IDS[TEST_IDS.ABSENCE_REMINDER_NEW_REQUEST_BUTTON],
                onPress: () => {
                    router.push({
                        pathname: "/leave-request",
                        params: {
                            childId: String(input.childId),
                            ...(classDate ? { dateFrom: classDate } : {}),
                        },
                    });
                },
            },
        ],
    );
}
